import type { RevenueSummaryItem, RevenueSummaryPeriod } from '@/api/models';

/**
 * Iconify identifier shown next to each KPI's title.
 *
 * Kept here (and not in JSX) so that the View remains render-only and free of
 * presentation-layer business rules — see the codebase split between
 * `*.tsx` files (render + localization only) and `*.ts` files (logic).
 */
export const REVENUE_SUMMARY_PERIOD_ICON: Record<RevenueSummaryPeriod, string> =
    {
        today: 'solar:sun-2-bold-duotone',
        thisWeek: 'solar:calendar-bold-duotone',
        thisMonth: 'solar:calendar-mark-bold-duotone',
        thisYear: 'solar:calendar-search-bold-duotone',
    };

/**
 * i18n key (relative to the `pages` namespace) for each period label.
 */
export const REVENUE_SUMMARY_PERIOD_LABEL_KEY: Record<
    RevenueSummaryPeriod,
    string
> = {
    today: 'sales.widgets.revenueSummary.today',
    thisWeek: 'sales.widgets.revenueSummary.thisWeek',
    thisMonth: 'sales.widgets.revenueSummary.thisMonth',
    thisYear: 'sales.widgets.revenueSummary.thisYear',
};

/**
 * Maps a `percentage` value coming from the API to the trend direction
 * understood by `TrendChip` / `KPI.Trend`.
 *
 * - positive → "up" (success color, ↑ arrow)
 * - negative → "down" (danger color, ↓ arrow)
 * - zero     → "neutral" (muted color, no arrow)
 * - null     → `null` — no comparable previous period, no trend to show
 */
export const getPercentageTrend = (
    percentage: number | null,
): 'down' | 'neutral' | 'up' | null => {
    if (percentage === null) {
        return null;
    }

    if (percentage > 0) {
        return 'up';
    }

    if (percentage < 0) {
        return 'down';
    }

    return 'neutral';
};

/**
 * The API returns raw percent values (e.g. `0.5` to mean `0.5%`). Intl's
 * `style: "percent"` formatter expects a fraction (e.g. `0.005`), so the View
 * passes values through this helper before handing them to `NumberValue`.
 */
export const toFractionalPercent = (percentage: number): number => {
    return percentage / 100;
};

/**
 * Whether a tile should carry a trend chip at all (BOS-170).
 *
 * "This month · GEL 0.00" used to sit under a red −100%. Both statements are
 * true and one of them is useless: the tile already says nothing was sold, and
 * saying it again in the colour reserved for things going wrong turns "the
 * month has just started" into an alarm. On the second of the month it fired
 * every year.
 *
 * A trend compares two numbers. With nothing on this side of the comparison
 * there is no rate of change to report — only the fact of the zero, which the
 * value itself carries.
 */
export const showsTrend = (
    item: RevenueSummaryItem,
    // A type guard, so the View can hand `changePercentage` straight to the
    // formatter without a second null check that could drift from this one.
): item is RevenueSummaryItem & { changePercentage: number } =>
    item.changePercentage !== null && item.amount !== 0;
